/**
 * Room on the chosen machine, before the start (ADR 0063 step 5): one quiet line while it has
 * room, and when it is full the person's choices: another machine with room, wait for room, or
 * start now anyway. It never blocks.
 */
import { RadioGroup, RadioGroupItem, StatusDot, cn } from '@orgus/ui';
import type { RoomChoice, RoomPlan } from './room.ts';

export function RoomNotice({ plan, choice, onChoice, disabled }: { plan: RoomPlan; choice: RoomChoice | null; onChoice: (c: RoomChoice) => void; disabled?: boolean }) {
  const v = plan.verdict;
  if (!v || !plan.line) return null;
  const a = v.admission;
  if (!plan.needsChoice) {
    const text = a.state === 'room' ? `${v.name} has room: ${plan.line.detail}` : `${v.name} is nearly full: ${plan.line.detail}`;
    return (
      <p className="-mt-2 flex min-w-0 items-center gap-2 text-caption text-fg-subtle" data-room={a.state}>
        <StatusDot tone={plan.line.tone} label={plan.line.label} />
        <span className="truncate">{text}</span>
      </p>
    );
  }
  const title = a.state === 'closed' ? `${v.name} is not taking work: ${plan.line.detail}` : `${v.name} is full: ${plan.line.detail}`;
  return (
    <div className="flex flex-col gap-3 rounded-card border border-border bg-raised px-4 py-3" role="group" aria-label={title} data-room={a.state}>
      <p className="flex min-w-0 items-center gap-2 text-ui text-fg">
        <StatusDot tone={plan.line.tone} label={plan.line.label} />
        <span className="min-w-0 truncate" title={title}>
          {title}
        </span>
      </p>
      <RadioGroup value={choice ?? undefined} onValueChange={(c) => onChoice(c as RoomChoice)} aria-label="When to start" disabled={disabled} className="gap-1">
        {plan.options.map((o) => {
          const id = `room-choice-${o.value}`;
          return (
            <label
              key={o.value}
              htmlFor={id}
              className={cn('flex min-w-0 cursor-pointer items-start gap-3 rounded-control px-2 py-1 hover:bg-hover', o.disabled && 'cursor-default opacity-60 hover:bg-transparent')}
            >
              <span className="flex h-5 shrink-0 items-center">
                <RadioGroupItem id={id} value={o.value} disabled={o.disabled} />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-ui text-fg">{o.label}</span>
                <span className="text-caption text-fg-subtle">{o.description}</span>
              </span>
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
